import type { MailboxInfo } from 'owa-client-types';
import { mutatorAction } from 'satcheljs';
import getCopilotResultsAccountState from '../selectors/getCopilotResultsAccountState';
import { default as getStore } from '../store/Store';
import type CopilotResultsStatus from '../store/schema/CopilotResultsStatus';
import type TableView from '../store/schema/TableView';

export const setCopilotResultsTableViewIdsMutator: (
    mailboxInfo: MailboxInfo,
    copilotResultsTableViewId: string | undefined, // Pass undefined to leave the existing tableViewId unchanged
    copilotResultsOriginTableViewId: string
) => void = mutatorAction(
    'setCopilotResultsTableViewIdsMutator',
    function setCopilotResultsTableViewIdsMutator(
        mailboxInfo: MailboxInfo,
        copilotResultsTableViewId: string | undefined,
        copilotResultsOriginTableViewId: string
    ): void {
        const state = getCopilotResultsAccountState(mailboxInfo);
        if (copilotResultsTableViewId) {
            state.copilotResultsTableViewId = copilotResultsTableViewId;
        }
        state.copilotResultsOriginTableViewId = copilotResultsOriginTableViewId;
    }
);

export const setCopilotResultsStatusMutator: (
    mailboxInfo: MailboxInfo,
    status: CopilotResultsStatus
) => void = mutatorAction(
    'setCopilotResultsStatusMutator',
    function setCopilotResultsStatusMutator(
        mailboxInfo: MailboxInfo,
        status: CopilotResultsStatus
    ): void {
        getCopilotResultsAccountState(mailboxInfo).copilotResultsStatus = status;
    }
);

// Resets the existing copilot results tableView's properties and sets the copilot results status to Loading.
// Callers are responsible for calling TableOperations.clear on the tableView before invoking this mutator.
export const prepareCopilotResultsTableForLoadingMutator: (
    mailboxInfo: MailboxInfo,
    tableView: TableView
) => void = mutatorAction(
    'prepareCopilotResultsTableForLoadingMutator',
    function prepareCopilotResultsTableForLoadingMutator(
        mailboxInfo: MailboxInfo,
        tableView: TableView
    ): void {
        tableView.isLoading = true;
        tableView.isInitialLoadComplete = false;
        tableView.isInCheckedMode = false;
        tableView.selectedRowKeys.clear();
        tableView.tableResetEpoch = (tableView.tableResetEpoch ?? 0) + 1;
        getCopilotResultsAccountState(mailboxInfo).copilotResultsStatus = 1;
    }
);

export const markCopilotResultsTableReadyMutator: (
    mailboxInfo: MailboxInfo,
    tableView: TableView
) => void = mutatorAction(
    'markCopilotResultsTableReadyMutator',
    function markCopilotResultsTableReadyMutator(
        mailboxInfo: MailboxInfo,
        tableView: TableView
    ): void {
        tableView.isLoading = false;
        tableView.isInitialLoadComplete = true;
        getCopilotResultsAccountState(mailboxInfo).copilotResultsStatus = 2;
    }
);

export const setCopilotResultsConversationIdMutator: (
    mailboxInfo: MailboxInfo,
    conversationId: string
) => void = mutatorAction(
    'setCopilotResultsConversationIdMutator',
    function setCopilotResultsConversationIdMutator(
        mailboxInfo: MailboxInfo,
        conversationId: string
    ): void {
        getCopilotResultsAccountState(mailboxInfo).copilotResultsConversationId = conversationId;
    }
);

// Removes the copilot results table view from the store map and clears all related state.
// Callers are responsible for calling TableOperations.clear on the tableView before invoking this mutator.
export const teardownCopilotResultsTableMutator: (mailboxInfo: MailboxInfo) => void = mutatorAction(
    'teardownCopilotResultsTableMutator',
    function teardownCopilotResultsTableMutator(mailboxInfo: MailboxInfo): void {
        const state = getCopilotResultsAccountState(mailboxInfo);
        if (state.copilotResultsTableViewId) {
            getStore().tableViews.delete(state.copilotResultsTableViewId);
        }
        state.copilotResultsTableViewId = undefined;
        state.copilotResultsOriginTableViewId = undefined;
        state.copilotResultsStatus = 0;
        state.copilotResultsConversationId = undefined;
    }
);
